import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import Slick from "vue-slick";
import Multiselect from "vue-multiselect";
import { validations, validationMessages } from "./validations";
import { MediaKindPlayer, TextEditor } from "@/components";
import { Validation } from "vuelidate";
import { IEmail } from "./IEmail";
import * as icons from "@/components/icons";
import { DomPurifyConstants} from "@/constants";
import {  sanitize } from "@/helpers";

import {
    IAffiliate,
    IBrand,
    IDecisionMessage,
    IMessage,
    IMessageReaction,
    IRequest,
    IRequestFile,
    IRequestStatus,
    ITheme,
    ITypeOfAssets,
    ITypeOfCampaign,
    IUser,
    Priority,
    RequestFileTypes,
    RequestStatus,
    Role,
} from "@/models";
import { IRequestFileViewModel, IViewRequestViewModel } from "./IViewRequestViewModel";

@Component({
    name: "view-request",
    components: {
        Slick,
        Multiselect,
        MediaKindPlayer,
        "view-icon": icons.ViewIcon,
        "download-icon": icons.DownloadIcon,
        "close-icon": icons.CloseIcon,
        "edit-icon": icons.EditIcon,
        "check-icon": icons.CheckIcon,
        "text-editor": TextEditor,
        "search-loop-icon": icons.SearchLoopIcon,
    },
    validations,
    data() {
        return {
            slickOptions: {
                slidesToShow: 1,
                arrows: true,
                asNavFor: ".slider-nav",
                fade: true,
            },
            slickNavOptions: {
                slidesToShow: 25,
                draggable: false,
                vertical: true,
                asNavFor: ".slider-for",
                focusOnSelect: true,
            },
            quillEditorOptions: {
                formats: ["bold", "italic", "underline", "link", "list"],
                modules: {
                    toolbar: ["bold", "italic", "underline", "link", { list: "bullet" }, { list: "ordered" }],
                },
                theme: "snow",
                matchVisual: false,
            },
        };
    },
})
export default class ViewRequest extends Vue {

    @Prop({ required: true, type: Number })
    public requestId: number;

    public promises: Array<Promise<any>>;
    public decisionDescription: string;
    public newMessage: IMessage;
    public editedMessage: IMessage;
    public email: IEmail;
    public finalStatus: IRequestStatus;
    public displayedIndexFile: number;
    private extensionRegexp = new RegExp("(?:\.([^.]+))?$");

    constructor() {
        super();

        this.promises = [];
        this.decisionDescription = "";
        this.newMessage = {
            createdBy: null,
            createdDate: null,
            messageId: 0,
            requestId: this.requestId,
            text: "",
            issue: false,
            updatedBy: null,
            updatedDate: null,
            reactions: [],
        };

        this.editedMessage = {
            createdBy: null,
            createdDate: null,
            messageId: 0,
            requestId: this.requestId,
            text: "",
            issue: false,
            updatedBy: null,
            updatedDate: null,
            reactions: [],
        };

        this.email = {
            copyAuthor: false,
            message: "",
            requestId: null,
            subject: "",
        };

        this.finalStatus = null;

        this.displayedIndexFile = 0;

        if (!this.$store.state.app.user) {
            this.promises.push(this.$store.dispatch("app/getUser"));
        }

        if (!this.$store.state.request.isReferenceDataLoaded) {
            this.promises.push(this.$store.dispatch("request/fetchReferenceData"));
        }
    }

    public get request(): IViewRequestViewModel {
        const singleRequest: IViewRequestViewModel = this.$store.state.request.singleRequest;

        if (singleRequest) {
            singleRequest.files = [...singleRequest.files.map((f: IRequestFile) => ({
                ...f,
                displayed: false,
            }))];
        }

        return this.$store.state.request.singleRequest;
    }

    public get themes(): ITheme[] {
        return this.$store.getters["request/getThemes"];
    }

    public get selectedThemes(): ITheme[] {
        return this.request?.themes ?? [];
    }

    public get orderedRequestFiles(): IRequestFileViewModel[] {
        return this.request?.files.sort((a, b) => b.fileType - a.fileType);
    }

    public get Priority(): typeof Priority {
        return Priority;
    }

    public get user(): IUser {
        return this.$store.state.app.user;
    }

    public get requestStatusClass(): string {
        return this.$options.filters
            .displayedStatus(this.request?.requestStatus, this.user?.role)
            .replace(" ", "-")
            .toLowerCase();
    }

    public get requestStatus(): IRequestStatus[] {
        return this.$store.state.request.requestStatus;
    }

    public get finalRequestStatus(): IRequestStatus[] {
        return this.requestStatus?.filter(s => s.step > 1 &&
            s.name !== RequestStatus.assessed.toString() &&
            s.name !== RequestStatus.canceled.toString())
            ?? [];
    }

    public get decisionMessages(): IDecisionMessage[] {
        return this.$store.getters["request/getDecisionMessages"]() || [];
    }

    public get messages(): IMessage[] {
            const messages = this.$store.getters["request/getRequestMessages"](this.requestId) || [];
            return messages.map((m: IMessage) => ({
                ...m,
                text: sanitize(m.text, DomPurifyConstants.globalMessageConfiguration),
                issue: m.issue
            }))
    }

    public get isLoaded(): boolean {
        return this.request?.requestId > 0;
    }

    public get isCreator(): boolean {
        return this.user?.userInfoId === this.request?.createdBy?.userInfoId;
    }

    public get isAdmin(): boolean {
        return this.user?.role === Role.Admin;
    }

    public get isVip(): boolean {
        return this.user?.role === Role.Vip;
    }

    public get isReadonly(): boolean {

        return !(this.isLoaded && !this.isVip &&
            (this.request.requestStatus.step < 1 ||
                this.isAdmin ||
                this.request?.requestStatus.name === RequestStatus.toModify.toString()));
    }

    public get canBeSentToPanel(): boolean {
        return !!this.request?.requestStatus &&
            (this.request?.requestStatus.name === RequestStatus.submitted);
    }

    public get canBeSentBack(): boolean {
        return !!this.request?.requestStatus &&
            this.request?.requestStatus.name === RequestStatus.submitted;
    }

    public get canCancelPanelReview(): boolean {
        return !!this.request?.requestStatus &&
        this.request?.requestStatus.name === RequestStatus.toReview;
    }

    public get canBeTerminated(): boolean {
        return !!this.request?.requestStatus &&
            this.request?.requestStatus.step === 1 &&
            (this.request?.requestStatus.name === RequestStatus.toReview ||
            (this.request.advice && this.request?.requestStatus.name !== RequestStatus.toModify));
    }

    public get canBeDeleted(): boolean {
        return !!this.request?.requestStatus &&
            (
                this.request?.requestStatus.step === 0 // Draft
                || this.user?.role === Role.Admin // Admin can Delete whenever they want
            );
    }

    public get canBeRejected(): boolean {
        return !!this.request?.requestStatus &&
            this.request?.requestStatus.step === 1 &&
            this.request?.requestStatus.name !== RequestStatus.toReview &&
            this.request?.requestStatus.name !== RequestStatus.submitted && // From submitted we can only cancel
            !this.request.advice;
    }

    public get canBeCanceled(): boolean {
        const availableStatus: RequestStatus[] = [RequestStatus.submitted, RequestStatus.toModify];
        return !!this.request?.requestStatus &&
            availableStatus.some(as => as === this.request?.requestStatus.name);
    }

    public get canAddNewMessage(): boolean {
        return this.isLoaded &&
            (this.isAdmin || this.isVip) &&
            this.request?.requestStatus.step === 1 &&
            // can not add message when editing another message
            this.editedMessage.messageId === 0;
    }

    public get validationMessages(): { [key: string]: string } {
        return validationMessages;
    }

    public get hasImage(): boolean {
        return this.request?.files?.length > 0;
    }

    public get isReady(): boolean {
        return this.isLoaded && this.request.requestId === this.requestId;
    }

    public get isCanceled(): boolean {
        return this.request?.requestStatus?.name === RequestStatus.canceled.toString();
    }

    public get creatorPhoto(): string {
        const imgLink = require("@/assets/img/default-profil-picture.png");
        return this.request?.creatorPhotoBase64 ?
            "data:image/png;base64, " + this.request?.creatorPhotoBase64 :
            imgLink;
    }

    // Hooks
    public async created(): Promise<void> {
        if (this.requestId === null || this.requestId === undefined || !isNaN(this.requestId)) {
            this.promises.push(this.$store.dispatch("request/fetchSingleRequest", this.requestId));
            await Promise.all(this.promises);
            if (!this.request) {
                this.$router.push({ name: "not-found" });
            }
        } else {
            this.$router.push({ name: "not-found" });
        }
    }

    public setDefaultEmailSubject(): void {
        this.email.subject = "Ask for information about the request: " + this.request?.campaignName;
    }

    public beforeRouteLeave(to, from, next) {
        this.$store.dispatch("request/clearAllSingleRequestData");
        next();
    }

    public getName(value: IAffiliate | IBrand | IUser): string {
        return value?.name ?? "";
    }

    public getValue(value: ITypeOfCampaign | ITypeOfAssets): string {
        return value?.value ?? "";
    }

    public async deleteRequest(): Promise<void> {
        if (await this.$store.dispatch("request/deleteUserRequest", this.requestId)) {
            this.$router.push("/list");
        }
    }

    public async showDeleteRequest(): Promise<void> {
        this.$modal.show("deleteRequest");
    }

    public async saveRequestAsToModify(): Promise<void> {
        this.request.requestStatus = this.requestStatus.find(s => s.name === RequestStatus.toModify);
        await this.submitRequest("request/createOrUpdateUserRequest");
    }

    public async saveRequestAsToReview(): Promise<void> {
        this.request.requestStatus = this.requestStatus.find(s => s.name === RequestStatus.toReview);
        await this.submitRequest("request/createOrUpdateUserRequest");
    }

    public async saveRequestAsSubmitted(): Promise<void> {
        this.request.requestStatus = this.requestStatus.find(s => s.name === RequestStatus.submitted);
        await this.submitRequest("request/createOrUpdateUserRequest");
    }

    public async terminateRequest(): Promise<void> {
        if (this.request.advice) {
            this.finalStatus = this.requestStatus.find(s => s.name === RequestStatus.assessed);
        }

        const finalStatusValidated: boolean = this.validateBeforeSubmit(this.$v.finalStatus);
        const decisionDescriptionValidated: boolean = this.validateBeforeSubmit(this.$v.decisionDescription);
        if (finalStatusValidated && decisionDescriptionValidated) {
            this.request.requestStatus = this.finalStatus;
            this.request.decisionDescription = this.decisionDescription;
            await this.submitRequest("request/createOrUpdateUserRequest");
        }
    }

    public async rejectRequest(): Promise<void> {
        if (this.validateBeforeSubmit(this.$v.decisionDescription)) {
            this.request.requestStatus = this.requestStatus.find(s => s.name === RequestStatus.rejected);
            this.request.decisionDescription = this.decisionDescription;
            await this.submitRequest("request/createOrUpdateUserRequest");
        }
    }

    public async cancelRequest(): Promise<void> {
        if (this.validateBeforeSubmit(this.$v.decisionDescription)) {
            this.request.requestStatus = this.requestStatus.find(s => s.name === RequestStatus.canceled);
            this.request.decisionDescription = this.decisionDescription;
            await this.submitRequest("request/createOrUpdateUserRequest");
        }
    }

    public setDecisionMessage(event): void {
        this.decisionDescription = event.target.value ?? "";
    }

    public async sendMessage(): Promise<void> {
        const sanitizedText = sanitize(this.newMessage.text);
        const messageText = this.newMessage.issue || sanitizedText.length > 0 ? sanitizedText : "<p><br></p>";

        if (!this.newMessage.issue || this.validateBeforeSubmit(this.$v.newMessage)) {
            const message: IMessage = {
                ...this.newMessage,
                text: messageText,
                issue: this.newMessage.issue,
            };

            await this.$store.dispatch("request/addRequestMessage", message);
            this.newMessage.text = "";
            this.$v.newMessage.$reset();
        }
    }

    public async sendEditMessage(): Promise<void> {
        if (this.validateBeforeSubmit(this.$v.editedMessage)) {
            const message: IMessage = {...this.editedMessage,
                text: sanitize(this.editedMessage.text, DomPurifyConstants.globalMessageConfiguration),
                issue:this.editedMessage.issue };

            await this.$store.dispatch("request/addRequestMessage", message);
            this.$v.editedMessage.$reset();

            this.editedMessage = {
                createdBy: null,
                createdDate: null,
                messageId: 0,
                requestId: this.requestId,
                issue: false,
                text: "",
                updatedBy: null,
                updatedDate: null,
                reactions: [],
            };
        }
    }

    public async sendEmailToRequester(request: IRequest): Promise<void> {
        this.email.requestId = this.request.requestId;
        await this.$store.dispatch("app/sendEmailToRequester", this.email);
        this.$modal.hide("sendEmail");
    }

    public clearEmail(): void {
        this.email.copyAuthor = false;
        this.email.message = "";
        this.email.requestId = null;
        this.email.subject = "";
    }

    public openEditMessage(message: IMessage): void {
        this.editedMessage = {...message};
    }

    public closeEditMessage() {
        this.editedMessage = {
            createdBy: null,
            createdDate: null,
            messageId: 0,
            requestId: this.requestId,
            issue: false,
            text: "",
            updatedBy: null,
            updatedDate: null,
            reactions: [],
        };
    }
    public canEditMessage(message: IMessage): boolean {
        return  message.createdBy.userInfoId === this.user.userInfoId && this.request?.requestStatus.step === 1;
    }

    public get canReactToMessage(): boolean {
        return this.request?.requestStatus.step === 1;
    }

    public async sendMessageReaction(message: IMessage): Promise<void> {
        const messageReaction: IMessageReaction = {
            messageId: message.messageId,
            requestId: this.requestId,
            createdBy: this.user,
        };
        await this.$store.dispatch("request/addRequestMessageReaction", messageReaction);
    }

    // Gallery
    public getImage(file: IRequestFile): string {
        const extension: string = this.extensionRegexp.exec(file.fileName)[1];
        return this.isImage(extension) ? file.uri : "";
    }

    public getImageClass(file: IRequestFile): string {
        const extension: string = this.extensionRegexp.exec(file.fileName)[1];
        if (this.isImage(extension)) {
            return "image";
        } else {
            return this.isExtensionType(extension) + " unknown-file-type";
        }
    }

    public getFileName(file: IRequestFile): string {
        return file?.fileType !== RequestFileTypes.Cover ? file?.fileName : "Cover Image";
    }

    // Stop video on slide
    public handleBeforeChangeMainSlider(event, slick, currentSlide) {
        const video = (slick.$slides[currentSlide].getElementsByClassName("bitmovinplayer-container")[0])?.children[0];
        if (video) {
            video.pause();
        }
    }

    // Add CSS class on slick nav
    public handleInit(event, slick) {
        return slick.$slides[0].classList.add("file-viewed"); // On active le viewed sur le first slide
    }

    public handleAfterChange(event, slick, currentSlide) {
        return slick.$slides[currentSlide].classList.add("file-viewed");
    }

    private validateBeforeSubmit(rules: Validation): boolean {
        rules.$touch();
        return !rules.$invalid;
    }

    private async submitRequest(action: string): Promise<void> {
        this.request.decisionDescription =
            sanitize(this.request.decisionDescription, DomPurifyConstants.globalMessageConfiguration);
        await this.$store.dispatch(action, this.request);
        this.$router.push("/list");
    }

    private isImage(extension: string): boolean {
        switch (extension?.toLowerCase()) {
            case "jpg":
            case "jpeg":
            case "gif":
            case "bmp":
            case "png": {
                return true;
            }
            default:
                return false;
        }
    }

    private isExtensionType(extension: string): string {
        switch (extension?.toLowerCase()) {
            case "pdf": {
                return "pdf";
            }
            case "mpeg":
            case "avi":
            case "mp4":
            case "m4v":
            case "mkv": {
                return "mp4";
            }
            case "mp3": {
                return "mp3";
            }
            case "doc":
            case "docx": {
                return "doc";
            }
            case "xls":
            case "xlsx": {
                return "xls";
            }
            case "ppt":
            case "pptx": {
                return "ppt";
            }
            default:
                return "";
        }
    }
}
